/**
 * The one way a chord gets into the session.
 *
 * There are two kinds of input and they must not become two kinds of app. A microphone —
 * and, later, MIDI — is a *continuous stream*: it produces a `DetectedChord` whenever the
 * sounding harmony settles, and it speaks through `ChordRecognizer`. A tap on a branch
 * card, a pick from the fast chord picker and a typed symbol are *discrete selections*:
 * there is no signal, no stability machine and nothing to be uncertain about.
 *
 * Both funnel through here into the same `CHORD_DETECTED`, which is why On the Go needed
 * no second harmony engine: branch matching, key estimation, respelling, pruning and the
 * branch request all happen exactly as they do at the piano. `source` is the only thing
 * that differs, and it changes only the label on the node.
 *
 * ## The clock is not an implementation detail
 *
 * `DetectedChord.timestamp` is documented as `performance.now()` milliseconds, and the
 * reducer *subtracts* two of them: a detection whose set strictly extends the current
 * node's and arrives within `arpeggioWindowMs` of it grows that node in place instead of
 * appending. Stamp a selection with `Date.now()` and the difference against a
 * recogniser-stamped node is around 1.7 × 10¹² — or, in the other direction, hugely
 * negative, which sails through the `<= arpeggioWindowMs` check and silently swallows the
 * chord the player just chose. The two clocks share no epoch and are not comparable, so
 * everything on this path reads the same monotonic clock the recogniser does.
 */

import type { CanonicalChord, DetectedChord } from '@hc/shared';
import { synthesizeDetection } from '../components/system/chordReading.js';
import type { SessionStore } from './store.js';

/**
 * The monotonic clock `DetectedChord.timestamp` is defined against.
 *
 * `Date.now()` is the fallback only for environments with no `performance` at all; there
 * it is the sole clock, so nothing can disagree with it.
 */
export function performanceNow(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

/**
 * A chord that arrived on a continuous stream. The recogniser-side twin of
 * `commitChordSelection`.
 *
 * It is a one-line function on purpose: having a named counterpart means the recogniser
 * subscription and the tap handler read as two calls to the same seam rather than one
 * call and one open-coded dispatch that can drift.
 */
export function commitDetection(store: SessionStore, detected: DetectedChord): void {
  store.dispatch({ type: 'CHORD_DETECTED', detected, source: 'audio' });
}

/**
 * A chord that was chosen: a branch card tapped, a chord picked, a symbol typed.
 *
 * `now` is injectable so tests can drive the arpeggio window deterministically; it must
 * return the same clock the recogniser stamps with. Returns the synthesised detection,
 * which is what the node will carry.
 */
export function commitChordSelection(
  store: SessionStore,
  chord: CanonicalChord,
  now: () => number = performanceNow,
): DetectedChord {
  // `synthesizeDetection` is shared with manual entry rather than re-implemented here:
  // its one-hot chroma is what a later correction gets re-scored against, and two
  // slightly different fabrications of that would be two different answers to the same
  // question. It keeps pitch classes only, though — it speaks the recogniser's language,
  // and the recogniser has no letters — so the spelling the player actually saw on the
  // card or in the picker rides alongside it as `chosen`. Without that, choosing D♭
  // yielded a node reading C♯: the reducer respells against a key estimate that includes
  // the new chord, and the label was written against the key before it.
  const detected = synthesizeDetection(chord, now());
  store.dispatch({ type: 'CHORD_DETECTED', detected, source: 'manual', chosen: chord });
  return detected;
}
